/**
 * Topics Service - Handles topic-related API calls
 */

import { userServiceApi } from '@/services/api';
import type { Topic, TopicsApiResponse, UserTopic, UserTopicsApiResponse } from '@/types/topic';

/**
 * Fetch all available topics from the server
 * @returns Promise with topics data
 */
export const getTopics = async (): Promise<TopicsApiResponse> => {
  console.log('🔄 [TopicService] Starting to fetch topics from API...');

  try {
    console.log('📤 [TopicService] Making GET request to /topics/');
    const response = await userServiceApi.get<Topic[]>('/topics/');

    console.log('📥 [TopicService] Response received:', {
      success: response.success,
      dataLength: response.data?.length || 0,
      message: response.message
    });

    if (response.success) {
      console.log('✅ [TopicService] Topics fetched successfully:', response.data);

      // Log first topic to verify structure
      if (response.data && response.data.length > 0) {
        console.log('🔍 [TopicService] First topic structure:', response.data[0]);
      }
    } else {
      console.log('❌ [TopicService] API returned error:', response.error);
    }

    return {
      success: response.success,
      data: response.data || [],
      message: response.message || (response.success ? 'Topics retrieved successfully' : 'Failed to retrieve topics')
    };
  } catch (error) {
    console.error('🚨 [TopicService] Error fetching topics:', error);
    console.error('🚨 [TopicService] Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });

    return {
      success: false,
      data: [],
      message: 'Failed to connect to server'
    };
  }
};

/**
 * Fetch user topics with selection state from the server
 * @returns Promise with user topics data including selection state
 */
export const getUserTopics = async (): Promise<UserTopicsApiResponse> => {
  console.log('🔄 [TopicService] Starting to fetch user topics from API...');

  try {
    console.log('📤 [TopicService] Making GET request to /user/topics/');
    const response = await userServiceApi.get<any>('/user/topics');

    console.log('📥 [TopicService] Response received:', {
      success: response.success,
      dataLength: response.data?.length || 0,
      message: response.message
    });

    if (response.success) {
      console.log('✅ [TopicService] Raw user topics payload:', response.data);
      console.log('✅ [TopicService] Payload type:', typeof response.data);
      console.log('✅ [TopicService] Is array?', Array.isArray(response.data));
      console.log('✅ [TopicService] Payload keys:', response.data ? Object.keys(response.data) : 'null');
    } else {
      console.log('❌ [TopicService] API returned error:', response.error);
    }

    // Normalize payload shape: backend returns { topics: [...], total_topics: number }
    let rawList: any = response.data;
    console.log('🔍 [TopicService] Initial rawList:', rawList);

    if (!Array.isArray(rawList) && rawList && Array.isArray(rawList.topics)) {
      console.log('🔄 [TopicService] Unwrapping topics array from object');
      rawList = rawList.topics;
    } else if (!Array.isArray(rawList) && rawList && Array.isArray(rawList.data)) {
      console.log('🔄 [TopicService] Unwrapping nested data array');
      rawList = rawList.data;
    }

    if (!Array.isArray(rawList)) {
      console.warn('⚠️ [TopicService] Unexpected user topics payload, defaulting to empty array');
      console.warn('⚠️ [TopicService] rawList type:', typeof rawList);
      console.warn('⚠️ [TopicService] rawList value:', rawList);
      rawList = [];
    }

    // Normalize state to boolean
    const normalized: UserTopic[] = (rawList as any[]).map((t) => ({
      topic_id: t.topic_id,
      topic_name: t.topic_name,
      state: t.state === true || t.state === 'selected'
    }));

    return {
      success: response.success,
      data: normalized,
      message: response.message || (response.success ? 'User topics retrieved successfully' : 'Failed to retrieve user topics')
    };
  } catch (error) {
    console.error('🚨 [TopicService] Error fetching user topics:', error);
    console.error('🚨 [TopicService] Error details:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });

    return {
      success: false,
      data: [],
      message: 'Failed to connect to server'
    };
  }
};

/**
 * Add topics to user's interests
 * @param topicIds Array of topic IDs to add
 * @returns Promise with operation result
 */
export const addUserTopics = async (topicIds: number[]): Promise<{ success: boolean; message?: string }> => {
  console.log('🔄 [TopicService] Starting to add user topics:', topicIds);

  if (topicIds.length === 0) {
    return { success: true, message: 'No topics to add' };
  }

  try {
    console.log('📤 [TopicService] Making POST request to /topics/ with topic_ids:', topicIds);
    const result = await userServiceApi.post('/topics/', { topic_ids: topicIds });

    console.log('📥 [TopicService] Response for adding topics:', {
      success: result.success,
      message: result.message,
      error: result.error
    });

    if (result.success) {
      console.log('✅ [TopicService] All topics added successfully');
      return {
        success: true,
        message: result.message || 'Topics added successfully'
      };
    } else {
      console.log('❌ [TopicService] Failed to add topics:', result.error);
      return {
        success: false,
        message: result.error || result.message || 'Failed to add topics'
      };
    }
  } catch (error) {
    console.error('🚨 [TopicService] Error adding user topics:', error);
    return {
      success: false,
      message: 'Failed to add topics'
    };
  }
};

/**
 * Remove topics from user's interests
 * @param topicIds Array of topic IDs to remove
 * @returns Promise with operation result
 */
export const removeUserTopics = async (topicIds: number[]): Promise<{ success: boolean; message?: string }> => {
  console.log('🔄 [TopicService] Starting to remove user topics:', topicIds);

  if (topicIds.length === 0) {
    return { success: true, message: 'No topics to remove' };
  }

  try {
    // Try batch delete first (if backend supports it)
    console.log('📤 [TopicService] Making DELETE request to /topics/ with topic_ids:', topicIds);
    const result = await userServiceApi.delete('/topics/', { topic_ids: topicIds });

    console.log('📥 [TopicService] Response for removing topics:', {
      success: result.success,
      message: result.message,
      error: result.error
    });

    if (result.success) {
      console.log('✅ [TopicService] All topics removed successfully');
      return {
        success: true,
        message: result.message || 'Topics removed successfully'
      };
    } else {
      console.log('❌ [TopicService] Failed to remove topics:', result.error);
      return {
        success: false,
        message: result.error || result.message || 'Failed to remove topics'
      };
    }
  } catch (error) {
    console.error('🚨 [TopicService] Error removing user topics:', error);
    return {
      success: false,
      message: 'Failed to remove topics'
    };
  }
};

/**
 * Default icon mapping for topics
 * This provides fallback icons for topics that don't have specific icons
 */
// Removed default icon/description helpers per user request
